import React, { createContext, useContext, useState, ReactNode } from 'react';

interface ErrorInfo {
  id: string;
  message: string;
  type: 'error' | 'warning' | 'info';
  timestamp: number;
  details?: string;
  retryAction?: () => void;
}

interface ErrorContextType {
  errors: ErrorInfo[];
  addError: (message: string, type?: 'error' | 'warning' | 'info', details?: string, retryAction?: () => void) => void;
  showErrorNotification: (message: string, type?: 'error' | 'warning' | 'info', details?: string, retryAction?: () => void) => void;
  removeError: (id: string) => void;
  clearAllErrors: () => void;
  hasErrors: boolean;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const useError = () => {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

interface ErrorProviderProps {
  children: ReactNode;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  const [errors, setErrors] = useState<ErrorInfo[]>([]);

  const addError = (
    message: string, 
    type: 'error' | 'warning' | 'info' = 'error',
    details?: string,
    retryAction?: () => void
  ) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const newError: ErrorInfo = {
      id,
      message,
      type,
      timestamp: Date.now(),
      details,
      retryAction
    };

    setErrors(prev => [...prev, newError]);

    // Автоматически удаляем ошибки через 10 секунд (кроме критических)
    if (type !== 'error' || !retryAction) {
      setTimeout(() => {
        removeError(id);
      }, 10000);
    }
  };

  const removeError = (id: string) => {
    setErrors(prev => prev.filter(error => error.id !== id));
  };

  const clearAllErrors = () => {
    setErrors([]);
  };

  const hasErrors = errors.length > 0;

  const value: ErrorContextType = {
    errors,
    addError,
    showErrorNotification: addError, // Аліас для зручності
    removeError,
    clearAllErrors,
    hasErrors
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
};
